/**
 * Cognito Authentication using AWS Amplify.
 *
 * Flow (matches DaruValidator pattern):
 * 1. Configure Amplify with Cognito User Pool + Federate OIDC
 * 2. On page load, check for current session
 * 3. If not authenticated, redirect to Cognito hosted UI (backed by Midway/Federate)
 * 4. After auth, Amplify handles code exchange and token storage
 * 5. API calls use the idToken from the Amplify session
 *
 * Note: Values like us-east-1_yBKPKL25P are replaced at deploy time by Katal's
 * deployTimeReplacements in the CDK stack.
 */
import Amplify from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: 'us-east-1',
    userPoolId: 'us-east-1_yBKPKL25P',
    userPoolWebClientId: 'edjbg0i5aip49ogp8m8q98oul',
    oauth: {
      domain: 'prod-bmr-website.auth.us-east-1.amazoncognito.com',
      scope: ['openid', 'email', 'profile'],
      redirectSignIn: 'https://metrics-insight-ai.aft.amazon.dev',
      redirectSignOut: 'https://metrics-insight-ai.aft.amazon.dev',
      responseType: 'code',
    },
  },
});

const REFRESH_INTERVAL = 1000 * 60 * 10; // 10 minutes
let refreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Aliases allowed to approve/reject customer-added business metrics.
 * This is a UI gate only — the Lambda enforces the real allowlist via the
 * APPROVER_ALIASES env var. Keep the two in sync.
 */
export const APPROVER_ALIASES = ['samasamb'];

export function isApprover(alias: string | null | undefined): boolean {
  return !!alias && APPROVER_ALIASES.includes(alias);
}

/**
 * Normalize a raw Cognito identifier to a bare Amazon alias.
 * Strips an email domain ("samasamb@amazon.com" -> "samasamb") and the
 * federated IdP prefix Cognito adds to usernames
 * ("BMRWebsite-Prod_samasamb" -> "samasamb").
 */
function normalizeAlias(value?: string | null): string {
  if (!value) return '';
  let alias = String(value);
  if (alias.includes('@')) alias = alias.split('@')[0];      // drop email domain
  if (alias.includes('_')) alias = alias.substring(alias.indexOf('_') + 1); // drop "IdpName_" prefix
  return alias;
}

/**
 * Read the current user's Amazon alias from the Cognito id token.
 * Order: federated identity subject (cleanest) -> email local-part ->
 * cognito:username (with IdP prefix stripped).
 */
export async function getCurrentUserAlias(): Promise<string> {
  try {
    const session = await Auth.currentSession();
    const payload: any = session.getIdToken().decodePayload();
    const federatedSubject: string | undefined = payload.identities?.[0]?.userId;
    if (federatedSubject) return normalizeAlias(federatedSubject);
    if (payload.email) return normalizeAlias(payload.email);
    return normalizeAlias(payload['cognito:username']);
  } catch {
    return '';
  }
}

/**
 * Get the current id_token. If not authenticated, triggers login redirect.
 */
export async function getToken(): Promise<string> {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    // Not authenticated - redirect to Cognito/Federate login
    await Auth.federatedSignIn();
    throw new Error('Redirecting to login...');
  }
}

/**
 * Make an authenticated fetch request with Cognito id_token.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token: string;
  try {
    token = await getToken();
  } catch {
    // Redirect is happening, return a never-resolving promise
    return new Promise(() => {});
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', token);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response = await fetch(url, { ...options, headers });

  // If 401/403, token might be expired - refresh and retry once
  if (response.status === 401 || response.status === 403) {
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const newToken = user.signInUserSession.idToken.jwtToken;
      headers.set('Authorization', newToken);
      response = await fetch(url, { ...options, headers });
    } catch {
      // Force re-login
      await Auth.federatedSignIn();
      return new Promise(() => {});
    }
  }

  return response;
}

/**
 * Initialize auth on page load.
 * Starts token refresh interval and checks current auth state.
 */
export async function initAuth(): Promise<void> {
  try {
    await Auth.currentSession();
    // Start refresh timer
    if (!refreshTimer) {
      refreshTimer = setInterval(async () => {
        try {
          await Auth.currentAuthenticatedUser({ bypassCache: true });
        } catch {
          console.error('Token refresh failed');
        }
      }, REFRESH_INTERVAL);
    }
  } catch {
    // Not authenticated yet - will redirect when API call is made
  }
}
